import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Clock, Download, LogIn, LogOut, Menu, Pencil, RefreshCw, Save, Search, ShieldCheck, Store, UserPlus, Users, X, XCircle } from "lucide-react";
import { appUserStatuses, dayLabels, leadStatuses, storeProfileTypeLabels, storeProfileTypes, storeStatuses } from "@xbolo/contracts";
import { api, type EmailStatusResponse, type MeResponse, type PortalLead, type PortalStore, type PortalUser, type SealResponse, type StoreHour } from "./api";
import { authConfig, completeLoginFromUrl, getStoredSession, isAdmin, logout, startLogin, type PortalSession } from "./auth";
import { mockLeads, mockMe, mockStores, mockUsers } from "./mock";

type Tab = "partner" | "admin-profiles" | "admin-leads" | "admin-users";

const emptyStoreForm = {
  slug: "",
  name: "",
  city: "",
  state: "SP",
  status: "waitlist",
  profileType: "authorized_producer"
};

export default function App() {
  const [session, setSession] = useState<PortalSession | null>(() => getStoredSession());
  const [me, setMe] = useState<MeResponse | null>(null);
  const [leads, setLeads] = useState<PortalLead[]>([]);
  const [stores, setStores] = useState<PortalStore[]>([]);
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [emailStatus, setEmailStatus] = useState<EmailStatusResponse["email"] | null>(null);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [tab, setTab] = useState<Tab>("partner");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  const admin = useMemo(() => Boolean(me?.permissions.admin || (session && isAdmin(session.claims))), [me?.permissions.admin, session]);
  const selectedStore = stores.find((store) => store.id === selectedStoreId) || stores[0] || null;
  const userLabel = me?.user.email || me?.user.name || session?.claims.email || session?.claims.name || "Usuario X-Bolo";

  function selectTab(nextTab: Tab) {
    setTab(nextTab);
    setNavOpen(false);
  }

  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("code")) return;
    completeLoginFromUrl(url)
      .then((nextSession) => {
        if (nextSession) {
          setSession(nextSession);
          window.history.replaceState({}, "", "/");
        }
      })
      .catch((error) => setStatus(error.message));
  }, []);

  useEffect(() => {
    if (!session) return;
    void loadInitial(session);
  }, [session]);

  async function loadInitial(currentSession = session) {
    if (!currentSession) return;
    setLoading(true);
    try {
      if (currentSession.mock) {
        const mock = mockMe(isAdmin(currentSession.claims));
        setMe(mock);
        setStores(mockStores);
        setLeads(mockLeads);
        setUsers(mockUsers);
        setEmailStatus({
          sender: "X-Bolo <no-reply@xbolo.com>",
          productionAccess: false,
          status: "demo",
          warning: "Demo: envio por email em modo restrito."
        });
        setSelectedStoreId(mockStores[0]?.id || "");
        setStatus("Modo demo local ativo.");
        return;
      }

      const nextMe = await api.me(currentSession);
      setMe(nextMe);
      setSelectedStoreId(nextMe.stores[0]?.id || "");
      if (nextMe.permissions.admin) {
        const [leadResponse, storeResponse, userResponse, emailResponse] = await Promise.all([
          api.adminLeads(currentSession),
          api.adminStores(currentSession),
          api.adminUsers(currentSession),
          api.adminEmailStatus(currentSession)
        ]);
        setLeads(leadResponse.leads);
        setStores(storeResponse.stores);
        setUsers(userResponse.users);
        setEmailStatus(emailResponse.email);
        setSelectedStoreId(nextMe.stores[0]?.id || storeResponse.stores[0]?.id || "");
      } else if (nextMe.stores[0]) {
        const storeResponse = await api.partnerStore(currentSession, nextMe.stores[0].id);
        setStores([storeResponse.store]);
      }
      setStatus("Dados atualizados.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Nao foi possivel carregar o painel.");
    } finally {
      setLoading(false);
    }
  }

  if (!session) {
    return <LoginScreen status={status} onLogin={() => void startLogin().catch((error) => setStatus(error.message))} />;
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <button
          className="icon-button topbar__menu"
          type="button"
          onClick={() => setNavOpen(true)}
          aria-label="Abrir menu"
          aria-controls="panel-sidebar"
          aria-expanded={navOpen}
        >
          <Menu size={20} />
        </button>
        <div className="topbar__brand">
          <BrandMark />
          <div>
            <span className="eyebrow">Painel operacional</span>
            <h1>Painel do Parceiro</h1>
          </div>
        </div>
        <div className="topbar__actions">
          <button className="icon-button" type="button" onClick={() => void loadInitial()} disabled={loading} title="Atualizar">
            <RefreshCw size={18} />
          </button>
        </div>
      </header>

      <main className="workspace">
        <button className={`sidebar-backdrop ${navOpen ? "is-open" : ""}`} type="button" aria-label="Fechar menu" onClick={() => setNavOpen(false)} />
        <aside id="panel-sidebar" className={`sidebar ${navOpen ? "is-open" : ""}`}>
          <div className="user-box">
            <ShieldCheck size={20} />
            <div>
              <strong>{userLabel}</strong>
              <span>{admin ? "Administrador" : "Parceiro"}</span>
            </div>
          </div>

          <nav className="nav-tabs" aria-label="Areas do painel">
            <button className={tab === "partner" ? "is-active" : ""} type="button" onClick={() => selectTab("partner")}>
              <Store size={18} />
              Minha loja
            </button>
            {admin && (
              <div className="nav-group" role="group" aria-label="Admin">
                <span>Admin</span>
                <button className={tab === "admin-profiles" ? "is-active" : ""} type="button" onClick={() => selectTab("admin-profiles")}>
                  <ShieldCheck size={18} />
                  Perfis
                </button>
                <button className={tab === "admin-leads" ? "is-active" : ""} type="button" onClick={() => selectTab("admin-leads")}>
                  <Search size={18} />
                  Leads
                </button>
                <button className={tab === "admin-users" ? "is-active" : ""} type="button" onClick={() => selectTab("admin-users")}>
                  <Users size={18} />
                  Usuarios
                </button>
              </div>
            )}
            <button className="nav-tabs__logout" type="button" onClick={logout}>
              <LogOut size={18} />
              Sair
            </button>
          </nav>

          <div className="status-box" aria-live="polite">
            {loading ? "Carregando..." : status || "Pronto."}
          </div>
        </aside>

        <section className="content">
          {tab === "partner" && selectedStore && (
            <PartnerPanel
              store={selectedStore}
              stores={stores}
              onSelectStore={setSelectedStoreId}
              onSaveProfile={(payload) => savePartnerProfile(selectedStore.id, payload)}
              onSaveHours={(hours) => saveHours(selectedStore.id, hours)}
            />
          )}
          {tab === "admin-leads" && admin && (
            <LeadsPanel
              leads={leads}
              onChangeStatus={(leadId, nextStatus) => changeLeadStatus(leadId, nextStatus)}
            />
          )}
          {tab === "admin-profiles" && admin && (
            <AdminProfilesPanel
              stores={stores}
              onCreateStore={createStore}
              onUpdateStore={updateStore}
              onGenerateSeal={generateAdminSeal}
              onRevokeSeal={revokeAdminSeal}
            />
          )}
          {tab === "admin-users" && admin && (
            <AdminUsersPanel
              users={users}
              stores={stores}
              emailStatus={emailStatus}
              onCreateUser={createUser}
              onUpdateUser={updateUser}
              onResendInvite={resendInvite}
              onSaveStoreLink={saveUserStoreLink}
              onRemoveStoreLink={removeUserStoreLink}
            />
          )}
        </section>
      </main>
    </div>
  );

  async function savePartnerProfile(storeId: string, payload: Record<string, unknown>) {
    if (!session) return;
    setLoading(true);
    try {
      const response = session.mock
        ? { store: { ...selectedStore!, ...payload } as PortalStore }
        : await api.updatePartnerStore(session, storeId, payload);
      setStores((items) => items.map((item) => (item.id === storeId ? response.store : item)));
      setStatus("Perfil publico atualizado.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Erro ao salvar perfil.");
    } finally {
      setLoading(false);
    }
  }

  async function saveHours(storeId: string, hours: StoreHour[]) {
    if (!session) return;
    setLoading(true);
    try {
      const response = session.mock
        ? { store: { ...selectedStore!, hours } as PortalStore }
        : await api.updateHours(session, storeId, hours);
      setStores((items) => items.map((item) => (item.id === storeId ? response.store : item)));
      setStatus("Horarios atualizados.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Erro ao salvar horarios.");
    } finally {
      setLoading(false);
    }
  }

  async function changeLeadStatus(leadId: string, nextStatus: string) {
    if (!session) return;
    if (session.mock) {
      setLeads((items) => items.map((lead) => (lead.id === leadId ? { ...lead, status: nextStatus } : lead)));
      setStatus("Status atualizado no demo.");
      return;
    }
    const response = await api.updateLead(session, leadId, nextStatus);
    setLeads((items) => items.map((lead) => (lead.id === leadId ? response.lead : lead)));
    setStatus("Lead atualizado.");
  }

  async function createStore(payload: Record<string, unknown>) {
    if (!session) return;
    setLoading(true);
    try {
      const response = session.mock
        ? { store: { ...mockStores[0], ...payload, id: crypto.randomUUID(), hours: [] } as PortalStore }
        : await api.createStore(session, payload);
      setStores((items) => [response.store, ...items]);
      setStatus("Perfil criado.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Erro ao criar perfil.");
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function updateStore(storeId: string, payload: Record<string, unknown>) {
    if (!session) return;
    setLoading(true);
    try {
      const response = session.mock
        ? { store: { ...stores.find((store) => store.id === storeId), ...payload } as PortalStore }
        : await api.updateAdminStore(session, storeId, payload);
      setStores((items) => items.map((item) => (item.id === storeId ? response.store : item)));
      setSelectedStoreId((current) => (current === storeId ? response.store.id : current));
      setStatus("Perfil atualizado.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Erro ao atualizar perfil.");
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function generateAdminSeal(storeId: string) {
    if (!session) return;
    setLoading(true);
    try {
      const response = session.mock
        ? mockSealResponse(storeId, stores)
        : await api.generateAdminStoreSeal(session, storeId);
      setStores((items) => items.map((item) => (item.id === storeId ? response.store : item)));
      setStatus(response.seal.url ? "Selo pronto para download." : "Selo gerado.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Erro ao gerar selo.");
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function revokeAdminSeal(storeId: string) {
    if (!session) return;
    setLoading(true);
    try {
      const response = session.mock
        ? { store: { ...stores.find((store) => store.id === storeId), certificate: { ...stores.find((store) => store.id === storeId)?.certificate, status: "revoked" } } as PortalStore }
        : await api.revokeAdminStoreSeal(session, storeId);
      setStores((items) => items.map((item) => (item.id === storeId ? response.store : item)));
      setStatus("Selo revogado. O QR nao valida mais no site.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Erro ao revogar selo.");
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function createUser(payload: Record<string, unknown>) {
    if (!session) return;
    setLoading(true);
    try {
      const response = session.mock
        ? { user: { ...mockUsers[0], ...payload, id: crypto.randomUUID(), status: "invited", inviteSentAt: new Date().toISOString(), storeLink: null } as PortalUser, warning: "" }
        : await api.createAdminUser(session, payload);
      setUsers((items) => [response.user, ...items]);
      setStatus(response.warning || "Usuario criado e convite solicitado.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Erro ao criar usuario.");
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function updateUser(userId: string, payload: Record<string, unknown>) {
    if (!session) return;
    setLoading(true);
    try {
      const response = session.mock
        ? { user: { ...users.find((user) => user.id === userId), ...payload } as PortalUser, warning: "" }
        : await api.updateAdminUser(session, userId, payload);
      setUsers((items) => items.map((item) => (item.id === userId ? response.user : item)));
      setStatus(response.warning || "Usuario atualizado.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Erro ao atualizar usuario.");
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function resendInvite(userId: string) {
    if (!session) return;
    setLoading(true);
    try {
      const response = session.mock
        ? { user: { ...users.find((user) => user.id === userId), inviteResentAt: new Date().toISOString() } as PortalUser, warning: "" }
        : await api.resendAdminUserInvite(session, userId);
      setUsers((items) => items.map((item) => (item.id === userId ? response.user : item)));
      setStatus(response.warning || "Convite reenviado.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Erro ao reenviar convite.");
    } finally {
      setLoading(false);
    }
  }

  async function saveUserStoreLink(userId: string, payload: Record<string, unknown>) {
    if (!session) return;
    setLoading(true);
    try {
      const response = session.mock
        ? { user: mockLinkedUser(users.find((user) => user.id === userId), stores, payload) }
        : await api.updateAdminUserStoreLink(session, userId, payload);
      setUsers((items) => items.map((item) => (item.id === userId ? response.user : item)));
      setStatus("Vinculo atualizado.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Erro ao salvar vinculo.");
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function removeUserStoreLink(userId: string) {
    if (!session) return;
    setLoading(true);
    try {
      const response = session.mock
        ? { user: { ...users.find((user) => user.id === userId), storeLink: null } as PortalUser }
        : await api.deleteAdminUserStoreLink(session, userId);
      setUsers((items) => items.map((item) => (item.id === userId ? response.user : item)));
      setStatus("Vinculo removido.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Erro ao remover vinculo.");
      throw error;
    } finally {
      setLoading(false);
    }
  }
}

function LoginScreen({ status, onLogin }: { status: string; onLogin: () => void }) {
  return (
    <main className="login-screen">
      <section className="login-panel">
        <BrandMark centered />
        <span className="eyebrow">Painel operacional</span>
        <h1>Painel do Parceiro</h1>
        <p>
          Acesso operacional para atualizar dados publicos de loja/produtor autorizado e acompanhar leads da marca.
        </p>
        <button className="button button--primary" type="button" onClick={onLogin}>
          <LogIn size={18} />
          {authConfig.mock ? "Entrar no modo demo" : "Entrar"}
        </button>
        {status && <p className="form-status">{status}</p>}
      </section>
    </main>
  );
}

function BrandMark({ centered = false }: { centered?: boolean }) {
  return (
    <div className={`brand-mark ${centered ? "brand-mark--centered" : ""}`} aria-label="X-Bolo original">
      <img src="/images/brand/xbolo-logo-original-transparent-360.webp" alt="X-Bolo original" />
    </div>
  );
}

function PartnerPanel({
  store,
  stores,
  onSelectStore,
  onSaveProfile,
  onSaveHours
}: {
  store: PortalStore;
  stores: PortalStore[];
  onSelectStore: (id: string) => void;
  onSaveProfile: (payload: Record<string, unknown>) => void;
  onSaveHours: (hours: StoreHour[]) => void;
}) {
  const [form, setForm] = useState(() => partnerFormFromStore(store));
  const [hours, setHours] = useState<StoreHour[]>(() => normalizeWeek(store.hours));

  useEffect(() => {
    setForm(partnerFormFromStore(store));
    setHours(normalizeWeek(store.hours));
  }, [store]);

  const canEditStore = store.role !== "viewer";

  return (
    <div className="panel-stack">
      <div className="section-head">
        <div>
          <span className="eyebrow">{store.profileLabel}</span>
          <h2>{store.name}</h2>
        </div>
        {stores.length > 1 && (
          <select value={store.id} onChange={(event) => onSelectStore(event.target.value)}>
            {stores.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="stats-grid">
        <Metric label="Status publico" value={store.storeStatus || store.status} />
        <Metric label="Cidade" value={`${store.city}/${store.state}`} />
        <Metric label="Verificação" value={store.certificate?.status || "sem verificação"} />
      </div>

      {store.certificate?.status === "active" && store.certificate.sealUrl && (
        <CertificateSealPanel store={store} />
      )}

      {!canEditStore && (
        <section className="notice">
          <strong>Acesso de leitura</strong>
          <span>Este usuario pode consultar as informacoes, mas nao pode salvar alteracoes neste perfil.</span>
        </section>
      )}

      <section className="surface">
        <div className="surface__head">
          <h3>Dados editaveis</h3>
          <button className="button button--primary" type="button" onClick={() => onSaveProfile(form)} disabled={!canEditStore}>
            <Save size={18} />
            Salvar perfil
          </button>
        </div>
        <div className="form-grid">
          <Field label="WhatsApp" value={form.whatsapp} onChange={(value) => setForm({ ...form, whatsapp: value })} />
          <Field label="Instagram" value={form.instagramUrl} placeholder="@perfil ou link" onChange={(value) => setForm({ ...form, instagramUrl: value })} />
          <Field label="URL do pedido" value={form.orderUrl} placeholder="site.com/pedido" onChange={(value) => setForm({ ...form, orderUrl: value })} />
          <Field label="Google Maps" value={form.mapsUrl} placeholder="maps.app.goo.gl/..." onChange={(value) => setForm({ ...form, mapsUrl: value })} />
          <Field label="Observacao" value={form.serviceNote} onChange={(value) => setForm({ ...form, serviceNote: value })} wide />
          <Toggle label="Pedido online disponivel" checked={form.onlineOrderAvailable} onChange={(value) => setForm({ ...form, onlineOrderAvailable: value })} />
          <Toggle label="Retirada" checked={form.pickupAvailable} onChange={(value) => setForm({ ...form, pickupAvailable: value })} />
          <Toggle label="Consumo local" checked={form.dineInAvailable} onChange={(value) => setForm({ ...form, dineInAvailable: value })} />
        </div>
      </section>

      <section className="surface">
        <div className="surface__head">
          <h3>Horario de atendimento</h3>
          <button className="button button--primary" type="button" onClick={() => onSaveHours(hours)} disabled={!canEditStore}>
            <Clock size={18} />
            Salvar horarios
          </button>
        </div>
        <div className="hours-grid">
          {hours.map((hour, index) => (
            <div className="hours-row" key={hour.dayOfWeek}>
              <div className="hours-row__day">
                <strong>{dayLabels[hour.dayOfWeek]}</strong>
                <label className="compact-check">
                  <input
                    type="checkbox"
                    checked={Boolean(hour.closed)}
                    onChange={(event) => setHours(updateHour(hours, index, { closed: event.target.checked }))}
                  />
                  Fechado
                </label>
              </div>
              <div className="hours-row__times">
                <label className="time-field">
                  <span>Abre</span>
                  <input
                    type="time"
                    value={hour.opensAt || ""}
                    disabled={Boolean(hour.closed)}
                    onChange={(event) => setHours(updateHour(hours, index, { opensAt: event.target.value }))}
                  />
                </label>
                <label className="time-field">
                  <span>Fecha</span>
                  <input
                    type="time"
                    value={hour.closesAt || ""}
                    disabled={Boolean(hour.closed)}
                    onChange={(event) => setHours(updateHour(hours, index, { closesAt: event.target.value }))}
                  />
                </label>
              </div>
              <input
                className="hours-row__note"
                placeholder="Nota"
                value={hour.note || ""}
                onChange={(event) => setHours(updateHour(hours, index, { note: event.target.value }))}
              />
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}

function CertificateSealPanel({ store }: { store: PortalStore }) {
  const sealUrl = store.certificate?.sealUrl || "";
  const canDownloadSeal = store.role === "owner" || store.role === "manager" || !store.role;

  return (
    <section className="surface">
      <div className="surface__head">
        <div>
          <span className="eyebrow">Verificação ativa</span>
          <h3>Selo de verificação</h3>
        </div>
        {canDownloadSeal ? (
          <a className="button button--primary" href={sealUrl} download>
            <Download size={18} />
            Baixar selo
          </a>
        ) : (
          <button className="button button--primary" type="button" disabled>
            <Download size={18} />
            Baixar selo
          </button>
        )}
      </div>
      <div className="public-preview">
        <strong>Selo de verificação X-Bolo original</strong>
        <span>{store.certificate?.verificationUrl || `Codigo ${store.certificate?.code || ""}`}</span>
      </div>
    </section>
  );
}

function LeadsPanel({ leads, onChangeStatus }: { leads: PortalLead[]; onChangeStatus: (leadId: string, status: string) => void }) {
  const [query, setQuery] = useState("");
  const filtered = leads.filter((lead) => `${lead.name} ${lead.city} ${lead.state} ${lead.phone} ${lead.email}`.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="panel-stack">
      <div className="section-head">
        <div>
          <span className="eyebrow">Admin</span>
          <h2>Leads</h2>
        </div>
        <label className="search-field">
          <Search size={18} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar lead" />
        </label>
      </div>
      <div className="stats-grid">
        <Metric label="Novos" value={String(leads.filter((lead) => lead.status === "new").length)} />
        <Metric label="Contatados" value={String(leads.filter((lead) => lead.status === "contacted").length)} />
        <Metric label="Total" value={String(leads.length)} />
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Cidade</th>
              <th>Contato</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((lead) => (
              <tr key={lead.id}>
                <td>
                  <strong>{lead.name}</strong>
                  <span>{formatDate(lead.createdAt)}</span>
                </td>
                <td>{lead.type}</td>
                <td>{lead.city}/{lead.state}</td>
                <td>{lead.phone}<br />{lead.email}</td>
                <td>
                  <select value={lead.status} onChange={(event) => onChangeStatus(lead.id, event.target.value)}>
                    {leadStatuses.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminProfilesPanel({
  stores,
  onCreateStore,
  onUpdateStore,
  onGenerateSeal,
  onRevokeSeal
}: {
  stores: PortalStore[];
  onCreateStore: (payload: Record<string, unknown>) => void | Promise<void>;
  onUpdateStore: (storeId: string, payload: Record<string, unknown>) => void | Promise<void>;
  onGenerateSeal: (storeId: string) => void | Promise<void>;
  onRevokeSeal: (storeId: string) => void | Promise<void>;
}) {
  const [storeForm, setStoreForm] = useState(emptyStoreForm);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingStore, setEditingStore] = useState<PortalStore | null>(null);

  function openCreateProfile() {
    setStoreForm(emptyStoreForm);
    setEditingStore(null);
    setCreateOpen(true);
  }

  function openEditProfile(store: PortalStore) {
    setStoreForm(storeFormFromStore(store));
    setEditingStore(store);
    setCreateOpen(true);
  }

  async function handleSaveProfile() {
    if (editingStore) {
      await onUpdateStore(editingStore.id, storeForm);
    } else {
      await onCreateStore(storeForm);
    }
    setStoreForm(emptyStoreForm);
    setEditingStore(null);
    setCreateOpen(false);
  }

  return (
    <div className="panel-stack">
      <div className="section-head">
        <div>
          <span className="eyebrow">Admin</span>
          <h2>Perfis</h2>
        </div>
        <button className="button button--primary" type="button" onClick={openCreateProfile}>
          <Store size={18} />
          Criar perfil
        </button>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Perfil</th>
              <th>Tipo</th>
              <th>Cidade</th>
              <th>Status</th>
              <th>Pedido</th>
              <th>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {stores.map((store) => (
              <tr key={store.id}>
                <td>
                  <strong>{store.name}</strong>
                  <span>{store.slug}</span>
                </td>
                <td>{store.profileLabel}</td>
                <td>{store.city}/{store.state}</td>
                <td>{store.storeStatus || store.status}</td>
                <td>{store.onlineOrderAvailable ? "ativo" : "inativo"}</td>
                <td>
                  <div className="table-actions">
                    <button className="icon-button table-action" type="button" onClick={() => openEditProfile(store)} title="Editar perfil">
                      <Pencil size={17} />
                    </button>
                    {store.certificate?.status === "active" && store.certificate.sealUrl ? (
                      <a className="button button--ghost table-button" href={store.certificate.sealUrl} download>
                        <Download size={16} />
                        Baixar selo
                      </a>
                    ) : (
                      <button className="button button--ghost table-button" type="button" onClick={() => void onGenerateSeal(store.id)}>
                        <Download size={16} />
                        Gerar selo
                      </button>
                    )}
                    {store.certificate?.status === "active" && (
                      <button className="button button--ghost table-button" type="button" onClick={() => void onRevokeSeal(store.id)}>
                        <XCircle size={16} />
                        Revogar selo
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <Modal title={editingStore ? "Editar perfil" : "Novo perfil"} onClose={() => setCreateOpen(false)}>
          <div className="form-grid">
            <Field label="Slug" value={storeForm.slug} onChange={(value) => setStoreForm({ ...storeForm, slug: value })} />
            <Field label="Nome publico" value={storeForm.name} onChange={(value) => setStoreForm({ ...storeForm, name: value })} />
            <Field label="Cidade" value={storeForm.city} onChange={(value) => setStoreForm({ ...storeForm, city: value })} />
            <Field label="Estado" value={storeForm.state} onChange={(value) => setStoreForm({ ...storeForm, state: value.toUpperCase() })} />
            <label className="field">
              <span>Status</span>
              <select value={storeForm.status} onChange={(event) => setStoreForm({ ...storeForm, status: event.target.value })}>
                {storeStatuses.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Tipo</span>
              <select value={storeForm.profileType} onChange={(event) => setStoreForm({ ...storeForm, profileType: event.target.value })}>
                {storeProfileTypes.map((item) => <option key={item} value={item}>{storeProfileTypeLabels[item]}</option>)}
              </select>
            </label>
          </div>
          <div className="modal-actions">
            <button className="button button--ghost" type="button" onClick={() => setCreateOpen(false)}>
              Cancelar
            </button>
            <button className="button button--primary" type="button" onClick={() => void handleSaveProfile()}>
              {editingStore ? <Save size={18} /> : <Store size={18} />}
              {editingStore ? "Salvar perfil" : "Criar perfil"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function AdminUsersPanel({
  users,
  stores,
  emailStatus,
  onCreateUser,
  onUpdateUser,
  onResendInvite,
  onSaveStoreLink,
  onRemoveStoreLink
}: {
  users: PortalUser[];
  stores: PortalStore[];
  emailStatus: EmailStatusResponse["email"] | null;
  onCreateUser: (payload: Record<string, unknown>) => void | Promise<void>;
  onUpdateUser: (userId: string, payload: Record<string, unknown>) => void | Promise<void>;
  onResendInvite: (userId: string) => void | Promise<void>;
  onSaveStoreLink: (userId: string, payload: Record<string, unknown>) => void | Promise<void>;
  onRemoveStoreLink: (userId: string) => void | Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<PortalUser | null>(null);
  const [createForm, setCreateForm] = useState({ name: "", email: "" });
  const [createError, setCreateError] = useState("");
  const [editForm, setEditForm] = useState({ name: "", status: "invited", storeId: "", role: "owner" });
  const createName = createForm.name.trim();
  const createEmail = createForm.email.trim().toLowerCase();
  const canCreateUser = Boolean(createName && createEmail.includes("@"));

  const filtered = users.filter((user) => {
    const matchesQuery = `${user.name} ${user.email} ${user.storeLink?.storeName || ""}`.toLowerCase().includes(query.toLowerCase());
    const matchesStatus = !statusFilter || user.status === statusFilter;
    return matchesQuery && matchesStatus;
  });

  function openEdit(user: PortalUser) {
    setEditingUser(user);
    setEditForm({
      name: user.name,
      status: user.status,
      storeId: user.storeLink?.storeId || "",
      role: user.storeLink?.role || "owner"
    });
  }

  async function handleCreate() {
    if (!canCreateUser) {
      setCreateError("Preencha nome e email validos antes de enviar o convite.");
      return;
    }

    try {
      setCreateError("");
      await onCreateUser({ name: createName, email: createEmail });
      setCreateForm({ name: "", email: "" });
      setCreateOpen(false);
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : "Nao foi possivel criar o usuario.");
    }
  }

  async function handleSaveUser() {
    if (!editingUser) return;
    await onUpdateUser(editingUser.id, { name: editForm.name, status: editForm.status });
    setEditingUser(null);
  }

  async function handleSaveLink() {
    if (!editingUser || !editForm.storeId) return;
    await onSaveStoreLink(editingUser.id, { storeId: editForm.storeId, role: editForm.role });
    setEditingUser(null);
  }

  async function handleRemoveLink() {
    if (!editingUser) return;
    await onRemoveStoreLink(editingUser.id);
    setEditingUser(null);
  }

  return (
    <div className="panel-stack">
      <div className="section-head">
        <div>
          <span className="eyebrow">Admin</span>
          <h2>Usuarios</h2>
        </div>
        <button className="button button--primary" type="button" onClick={() => { setCreateError(""); setCreateOpen(true); }}>
          <UserPlus size={18} />
          Criar usuario
        </button>
      </div>

      {emailStatus?.warning && (
        <section className="notice">
          <strong>Envio de convites em modo restrito</strong>
          <span>{emailStatus.warning}</span>
        </section>
      )}

      <section className="surface">
        <div className="filters-row">
          <label className="search-field">
            <Search size={18} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar usuario" />
          </label>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Filtrar status">
            <option value="">Todos os status</option>
            {appUserStatuses.map((item) => <option key={item} value={item}>{userStatusLabel(item)}</option>)}
          </select>
        </div>
      </section>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Status</th>
              <th>Convite</th>
              <th>Perfil vinculado</th>
              <th>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((user) => (
              <tr key={user.id}>
                <td>
                  <strong>{user.name}</strong>
                  <span>{user.email}</span>
                  {user.lastInviteError && <span className="text-danger">{user.lastInviteError}</span>}
                </td>
                <td>
                  {userStatusLabel(user.status)}
                  <span>{user.cognitoStatus || "sem status"}</span>
                  <span>{user.cognitoEnabled ? "login habilitado" : "login bloqueado"}</span>
                </td>
                <td>
                  {formatDate(user.inviteResentAt || user.inviteSentAt || "") || "nao enviado"}
                  <span>Ultimo login: {formatDate(user.lastLoginAt || "") || "sem acesso"}</span>
                </td>
                <td>
                  {user.storeLink ? (
                    <>
                      <strong>{user.storeLink.storeName}</strong>
                      <span>{storeRoleLabel(user.storeLink.role)} · {user.storeLink.city}/{user.storeLink.state}</span>
                    </>
                  ) : (
                    <span>Sem perfil vinculado</span>
                  )}
                </td>
                <td>
                  <div className="table-actions">
                    <button className="icon-button table-action" type="button" onClick={() => openEdit(user)} title="Editar usuario">
                      <Pencil size={17} />
                    </button>
                    {user.cognitoStatus !== "CONFIRMED" && (
                      <button className="button button--ghost table-button" type="button" onClick={() => void onResendInvite(user.id)}>
                        <RefreshCw size={16} />
                        Reenviar
                      </button>
                    )}
                    <button
                      className="button button--ghost table-button"
                      type="button"
                      onClick={() => void onUpdateUser(user.id, { status: user.status === "suspended" ? "active" : "suspended" })}
                    >
                      {user.status === "suspended" ? "Reativar" : "Suspender"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <Modal title="Criar usuario parceiro" onClose={() => setCreateOpen(false)}>
          <div className="form-grid">
            <Field label="Nome" value={createForm.name} onChange={(value) => { setCreateError(""); setCreateForm({ ...createForm, name: value }); }} />
            <Field label="Email" value={createForm.email} onChange={(value) => { setCreateError(""); setCreateForm({ ...createForm, email: value }); }} />
          </div>
          {createError && <p className="form-error">{createError}</p>}
          <div className="modal-actions">
            <button className="button button--ghost" type="button" onClick={() => setCreateOpen(false)}>
              Cancelar
            </button>
            <button className="button button--primary" type="button" onClick={() => void handleCreate()} disabled={!canCreateUser}>
              <UserPlus size={18} />
              Criar e enviar convite
            </button>
          </div>
        </Modal>
      )}

      {editingUser && (
        <Modal title="Editar usuario parceiro" onClose={() => setEditingUser(null)}>
          <div className="form-grid">
            <Field label="Nome" value={editForm.name} onChange={(value) => setEditForm({ ...editForm, name: value })} />
            <label className="field">
              <span>Email</span>
              <input value={editingUser.email} disabled readOnly />
            </label>
            <label className="field">
              <span>Status</span>
              <select value={editForm.status} onChange={(event) => setEditForm({ ...editForm, status: event.target.value })}>
                {appUserStatuses.map((item) => <option key={item} value={item}>{userStatusLabel(item)}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Perfil vinculado</span>
              <select value={editForm.storeId} onChange={(event) => setEditForm({ ...editForm, storeId: event.target.value })}>
                <option value="">Sem perfil</option>
                {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Funcao no perfil</span>
              <select value={editForm.role} onChange={(event) => setEditForm({ ...editForm, role: event.target.value })}>
                <option value="owner">Responsavel</option>
                <option value="manager">Gestor</option>
                <option value="viewer">Leitura</option>
              </select>
            </label>
          </div>
          <div className="modal-actions">
            {editingUser.storeLink && (
              <button className="button button--ghost" type="button" onClick={() => void handleRemoveLink()}>
                Remover vinculo
              </button>
            )}
            <button className="button button--ghost" type="button" onClick={() => void handleSaveUser()}>
              <Save size={18} />
              Salvar usuario
            </button>
            <button className="button button--primary" type="button" onClick={() => void handleSaveLink()} disabled={!editForm.storeId}>
              <Store size={18} />
              Salvar vinculo
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <h3 id="modal-title">{title}</h3>
          <button className="icon-button" type="button" onClick={onClose} title="Fechar">
            <X size={18} />
          </button>
        </div>
        {children}
      </section>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Field({ label, value, placeholder, wide, onChange }: { label: string; value: string; placeholder?: string; wide?: boolean; onChange: (value: string) => void }) {
  return (
    <label className={`field ${wide ? "field--wide" : ""}`}>
      <span>{label}</span>
      <input value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function partnerFormFromStore(store: PortalStore) {
  return {
    whatsapp: store.whatsapp || store.phone || "",
    instagramUrl: store.instagramUrl || "",
    orderUrl: store.orderUrl || "",
    mapsUrl: store.mapsUrl || "",
    serviceNote: store.serviceNote || "",
    dineInAvailable: Boolean(store.dineInAvailable),
    pickupAvailable: Boolean(store.pickupAvailable),
    onlineOrderAvailable: Boolean(store.onlineOrderAvailable)
  };
}

function storeFormFromStore(store: PortalStore) {
  return {
    slug: store.slug || "",
    name: store.name || "",
    city: store.city || "",
    state: store.state || "SP",
    status: store.storeStatus || store.status || "waitlist",
    profileType: store.profileType || "authorized_producer"
  };
}

function mockSealResponse(storeId: string, stores: PortalStore[]): SealResponse {
  const store = stores.find((item) => item.id === storeId) || mockStores[0];
  const certificate = {
    ...(store.certificate || {
      code: "xb_demo_public_code",
      status: "active",
      title: "Loja verificada X-Bolo original"
    }),
    sealUrl: "https://xbolo.com/certificates/xb_demo_public_code/selo-xbolo-original.png",
    sealKey: "certificates/xb_demo_public_code/selo-xbolo-original.png",
    sealGeneratedAt: new Date().toISOString(),
    verificationUrl: "https://xbolo.com/verificacao/?c=xb_demo_public_code"
  };
  return {
    seal: {
      key: certificate.sealKey,
      url: certificate.sealUrl,
      generatedAt: certificate.sealGeneratedAt,
      verificationUrl: certificate.verificationUrl
    },
    certificate,
    store: {
      ...store,
      certificate
    }
  };
}

function mockLinkedUser(user: PortalUser | undefined, stores: PortalStore[], payload: Record<string, unknown>): PortalUser {
  const store = stores.find((item) => item.id === payload.storeId) || stores[0];
  return {
    ...(user || mockUsers[0]),
    storeLink: store ? {
      id: "mock-link",
      storeId: store.id,
      storeSlug: store.slug,
      storeName: store.name,
      city: store.city,
      state: store.state,
      role: String(payload.role || "owner"),
      status: "active"
    } : null
  };
}

function normalizeWeek(hours: StoreHour[]) {
  return Array.from({ length: 7 }, (_, dayOfWeek) => {
    const existing = hours.find((hour) => hour.dayOfWeek === dayOfWeek);
    return existing || { dayOfWeek, opensAt: "10:00", closesAt: "18:00", closed: dayOfWeek === 0, note: dayOfWeek === 0 ? "Fechado" : "" };
  });
}

function updateHour(hours: StoreHour[], index: number, patch: Partial<StoreHour>) {
  return hours.map((hour, currentIndex) => (currentIndex === index ? { ...hour, ...patch } : hour));
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(date);
}

function userStatusLabel(status: string) {
  const labels: Record<string, string> = {
    active: "Ativo",
    invited: "Convidado",
    suspended: "Suspenso",
    revoked: "Revogado"
  };
  return labels[status] || status;
}

function storeRoleLabel(role: string) {
  const labels: Record<string, string> = {
    owner: "Responsavel",
    manager: "Gestor",
    viewer: "Leitura"
  };
  return labels[role] || role;
}
